/**
 * Turns whatever came back into something a person can act on.
 *
 * The harness relays some upstream failures verbatim, so its `error` field can
 * be a whole provider payload stringified with a status in front —
 * `503 {"error":{"type":"api_error","message":"service unavailable"}}` was
 * rendering into the transcript exactly like that.
 *
 * Messages the harness writes itself are already good ("The agent is at session
 * capacity — try again later") and are passed through untouched. Only text that
 * looks machine-generated gets rewritten.
 */

/**
 * A provider SDK's own words for a cancelled request, passed through by the
 * harness. They differ by timing — "Request aborted" before the first token,
 * "Request was aborted." mid-stream — though the person pressed the same Stop
 * either way (OPE-5443).
 */
const SDK_ABORT = /^request (was )?aborted\.?$/i;

/** A status code at the front, or embedded JSON, means nobody wrote this for a reader. */
const looksMachineGenerated = (text: string): boolean =>
	/^\d{3}\b/.test(text.trim()) || text.indexOf('{"') !== -1;

/** Pull the deepest `message` out of an embedded payload, if there is one. */
const extractMessage = (text: string): string => {
	const start = text.indexOf('{');
	if (start === -1) {
		return '';
	}
	try {
		const parsed = JSON.parse(text.slice(start));
		const nested = parsed && parsed.error;
		if (nested && typeof nested.message === 'string') {
			return nested.message;
		}
		if (typeof parsed.message === 'string') {
			return parsed.message;
		}
		if (typeof nested === 'string') {
			return nested;
		}
	} catch (parseError) {
		// Not parseable — the status-code branch below still applies.
	}
	return '';
};

const statusOf = (text: string): number => {
	const match = /^(\d{3})\b/.exec(text.trim());
	return match ? Number(match[1]) : 0;
};

export const humanizeAgentError = (raw: string): string => {
	const text = (raw || '').trim();
	if (!text) {
		return 'Something went wrong. Try again.';
	}
	if (SDK_ABORT.test(text)) {
		return 'You stopped this reply.';
	}
	if (!looksMachineGenerated(text)) {
		return text;
	}

	const status = statusOf(text);
	const inner = extractMessage(text).toLowerCase();

	if (status === 503 || inner.indexOf('service unavailable') !== -1) {
		return 'The AI service is temporarily unavailable. Try again in a moment.';
	}
	if (status === 429 || inner.indexOf('rate limit') !== -1 || inner.indexOf('overloaded') !== -1) {
		return 'The AI service is busy right now. Try again shortly.';
	}
	if (status === 401 || status === 403) {
		return 'Your session was refused. Sign in to OpenWrench again, then reopen the assistant.';
	}
	if (status >= 500) {
		return 'The AI service hit an error. Try again in a moment.';
	}

	const message = extractMessage(text);
	return message || 'Something went wrong. Try again.';
};

/**
 * What kind of failure this is, and therefore what the user can do about it.
 *
 * The distinction that matters is **retryable or not**: a capacity blip clears
 * on its own, so offering "Try again" is honest. An expired OpenWrench session
 * never clears from in here — the token the harness exchanges comes from the web
 * app's own cache — so a retry button would just fail again, and the only real
 * instruction is to sign in to OpenWrench.
 */
export type AgentErrorKind = 'signedOut' | 'capacity' | 'busy' | 'unreachable' | 'unknown';

export interface AgentErrorInfo {
	kind: AgentErrorKind;
	/** Already humanized — safe to render as-is. */
	message: string;
	/** Label for the retry action, or empty when retrying cannot help. */
	retryLabel: string;
}

const TRY_AGAIN = 'Try again';

/**
 * Classifies a rejected harness call. `agentJson` puts the HTTP status on the
 * error, so most of this reads that rather than the message; a `fetch` that
 * rejects before any response has no status at all, which is what distinguishes
 * "the network is down" from "the service said no".
 */
export const classifyAgentError = (error: any): AgentErrorInfo => {
	const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
	const status = error && typeof error.status === 'number' ? error.status : statusOf(raw);
	const lower = raw.toLowerCase();

	if (lower.indexOf('not signed in') !== -1) {
		return {
			kind: 'signedOut',
			message: 'You are signed out of OpenWrench. Sign in again to use Oscar.',
			retryLabel: '',
		};
	}
	if (status === 401 || status === 403) {
		return {
			kind: 'signedOut',
			message: 'Your OpenWrench session expired. Sign in again to use Oscar.',
			retryLabel: '',
		};
	}
	if (status === 429 || lower.indexOf('rate limit') !== -1) {
		return {
			kind: 'busy',
			message: 'Oscar is handling too many requests. Wait a moment, then try again.',
			retryLabel: TRY_AGAIN,
		};
	}
	// The harness returns 503 both for its own session capacity (MAX_SESSIONS)
	// and for a relayed provider outage, so the wording is taken from the message
	// rather than the status.
	if (lower.indexOf('capacity') !== -1) {
		return {
			kind: 'capacity',
			message: 'Oscar is at capacity right now.',
			retryLabel: TRY_AGAIN,
		};
	}
	if (status >= 500) {
		return {
			kind: 'capacity',
			message: 'Oscar is temporarily unavailable.',
			retryLabel: TRY_AGAIN,
		};
	}
	if (!status) {
		return {
			kind: 'unreachable',
			message: "Can't reach Oscar. Check your connection, then try again.",
			retryLabel: TRY_AGAIN,
		};
	}
	return { kind: 'unknown', message: humanizeAgentError(raw), retryLabel: TRY_AGAIN };
};

export default humanizeAgentError;
